import React, { useState, useEffect, useRef } from 'react';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { printPick6Coupon } from './Pick6Coupon';
import { PICK6_BOXES, PICK6_PICKS, PICK6_SECONDS } from '../utils/pick6';

const fmt = (v) => (Math.round(Number(v) * 100) / 100).toLocaleString();

// One Pick 6 round on the Coupon page. The customer marks any 6 of the numbers
// 1–40 and Confirm makes the sale: the server then decides what the coupon pays
// and draws its 20 winning numbers, so the choice never changes the payout. The
// 20-second timer runs until Confirm, like the mobile pick screen.
export default function Pick6Round({ product, optionCount, store, userName, onSell, onTimeout, onNewRound }) {
  const [picked, setPicked] = useState([]); // the customer's numbers, in the order marked
  const [sale, setSale] = useState(null);
  const [selling, setSelling] = useState(false);
  const [seconds, setSeconds] = useState(PICK6_SECONDS);
  const [note, setNote] = useState('');
  const sellingRef = useRef(false); // a fast double click must not sell twice
  const timedOutRef = useRef(false);

  useEffect(() => {
    if (sale || selling) return undefined;
    if (seconds <= 0) {
      if (!timedOutRef.current) {
        timedOutRef.current = true;
        onTimeout();
      }
      return undefined;
    }
    const t = setTimeout(() => setSeconds(s => s - 1), 1000);
    return () => clearTimeout(t);
  }, [seconds, sale, selling, onTimeout]);

  const full = picked.length === PICK6_PICKS;
  const matched = new Set(sale?.matched || []);
  const factor = sale?.factor || product?.priceAmount || 1;

  const toggle = (n) => {
    if (sale || sellingRef.current || timedOutRef.current) return;
    setPicked(prev => {
      if (prev.includes(n)) return prev.filter(x => x !== n);
      return prev.length < PICK6_PICKS ? [...prev, n] : prev;
    });
  };

  const confirm = async () => {
    if (!full || sale || sellingRef.current || timedOutRef.current) return;
    sellingRef.current = true;
    setSelling(true);
    const r = await onSell(picked);
    if (!r) return; // the page says why and closes the round
    setSale(r);
    setSelling(false);
  };

  const print = () => {
    const printed = printPick6Coupon({
      txnId: sale.id,
      storeSeq: sale.storeSeq,
      storeCode: store?.code,
      timestamp: sale.soldAt,
      userName,
      storeName: store?.name,
      productName: sale.productName || product?.name,
      picks: sale.picks,
      options: sale.options,
      factor,
    });
    setNote(printed ? '' : 'The browser blocked the print window. Allow pop-ups for this site, then press Print again.');
  };

  const left = PICK6_PICKS - picked.length;
  let prompt = left === PICK6_PICKS ? `Pick any ${PICK6_PICKS} numbers` : `Pick ${left} more number${left === 1 ? '' : 's'}`;
  if (selling) prompt = 'Recording the sale…';
  else if (sale && sale.isWinner) {
    prompt = sale.matched.length > 1 ? 'The gold numbers are winning numbers' : 'The gold number is a winning number';
  } else if (sale) prompt = 'None of the numbers is a winning number';
  else if (full) prompt = `All ${PICK6_PICKS} numbers chosen — press Confirm`;

  const shown = [...(sale?.picks || picked)]; // in the order they were chosen

  return (
    <div className="card mb-24">
      <div className={`card-title ${sale && sale.isWinner ? 'green' : 'gold'}`}>
        {sale ? (sale.isWinner ? 'Winner!' : 'Better luck next time') : 'Pick Your Numbers'}
      </div>
      <div className="card-body">
        <div className="pick6-layout">
          <div>
            <div className="pick6-head">
              <div>
                <div className="pick6-title">{sale ? 'Round finished' : `${product?.name || ''} — pick ${PICK6_PICKS} numbers`}</div>
                <div className="text-sm text-muted" aria-live="polite">{prompt}</div>
              </div>
              <div className="pick6-chips">
                <span className={`chip ${full ? 'chip-green' : 'chip-gray'}`}>{picked.length} / {PICK6_PICKS}</span>
                {!sale && (
                  <span className="chip chip-red"><TimerOutlinedIcon style={{ fontSize: 14 }} /> {seconds}s</span>
                )}
              </div>
            </div>
            <div className="pick6-grid">
              {Array.from({ length: PICK6_BOXES }, (_, i) => {
                const n = i + 1;
                const sel = picked.includes(n);
                let cls = 'pick6-num';
                if (sel) cls += ' sel';
                if (sale) cls += sel ? (matched.has(n) ? ' win' : ' miss') : ' dim';
                return (
                  <button key={n} type="button" className={cls} aria-pressed={sel} aria-label={`Number ${n}`}
                    disabled={!!sale || selling || (!sel && full)} onClick={() => toggle(n)}>
                    {n}
                    {sel && <CheckCircleIcon className="pick6-tick" aria-hidden="true" />}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="pick6-side">
            {sale && (sale.isWinner ? (
              <div className="pick6-result win">
                <EmojiEventsOutlinedIcon style={{ fontSize: 30, color: '#c9a227' }} />
                <div className="pick6-result-badge">WINNER</div>
                <div className="pick6-result-prize">{fmt(sale.prize)} P</div>
                {sale.matched.map((n, idx) => (
                  <div key={n} className="pick6-result-line">
                    <span>Number {n}</span><strong>{fmt(sale.matchedPrizes[idx] * factor)} P</strong>
                  </div>
                ))}
              </div>
            ) : (
              <div className="pick6-result lose">
                <div className="pick6-result-badge">NO PRIZE</div>
                <div className="text-sm">None of the 6 numbers is a winning number.</div>
              </div>
            ))}

            <div className="pick6-panel">
              <div className="pick6-panel-label">Your selected numbers</div>
              <div className="pick6-slots">
                {Array.from({ length: PICK6_PICKS }, (_, k) => (
                  shown[k] != null
                    ? <div key={k} className="pick6-slot filled">{shown[k]}</div>
                    : <div key={k} className="pick6-slot">–</div>
                ))}
              </div>
            </div>

            <div className="pick6-panel pick6-info">
              <div><span>Product</span><strong>{product?.name}</strong></div>
              <div><span>Price</span><strong>{product?.rsp}</strong></div>
              <div><span>Winning numbers</span><strong>{optionCount}</strong></div>
              {sale && <div><span>Card No</span><strong>{sale.id}</strong></div>}
            </div>

            {sale ? (
              <div className="pick6-actions">
                <button type="button" className="btn btn-gold btn-lg" onClick={print}>
                  <PrintOutlinedIcon style={{ fontSize: 18 }} /> Print Coupon
                </button>
                <button type="button" className="btn btn-primary" onClick={onNewRound}>New Round</button>
              </div>
            ) : (
              <div className="pick6-actions">
                <button type="button" className="btn btn-success btn-lg" disabled={!full || selling} onClick={confirm}>
                  {selling ? 'Recording…' : full ? 'Confirm' : `Pick ${left} more`}
                </button>
              </div>
            )}
            {note && <div className="pick6-note">{note}</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
